import React from 'react';
import { Scenario } from '../../types';
import { playUiSfx } from '../../utils/uiAudio';
import { ModalShell, PanelHeader, buttons, surfaces, cn } from './kit';

interface ScenarioBriefingModalProps {
    scenario: Scenario;
    onStart: () => void;
    onSkip?: () => void;
    onBackToMenu?: () => void;
}

const ScenarioBriefingModal: React.FC<ScenarioBriefingModalProps> = ({
    scenario,
    onStart,
    onSkip,
    onBackToMenu,
}) => {
    return (
        <ModalShell
            variant="center"
            zLevel="menu"
            overlayClassName="bg-black/90 overflow-y-auto overscroll-contain"
            className="max-w-2xl"
        >
            <div className={cn(surfaces.panel, 'border-indigo-500 p-8 shadow-[0_0_50px_rgba(99,102,241,0.2)] relative overflow-hidden my-4')}>
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 to-purple-600" />

                <PanelHeader
                    title="Mission Briefing"
                    subtitle={scenario.title}
                    className="border-0 px-0 pt-2 pb-6"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Situation</h3>
                        <p className="text-slate-300 text-sm leading-relaxed border-l-2 border-indigo-500/30 pl-4">
                            {scenario.description}
                        </p>
                        <div className="mt-4 text-xs font-mono text-slate-400">
                            <div>TIMELINE: {scenario.timeline}</div>
                            <div>
                                DIFFICULTY:{' '}
                                <span className={scenario.difficulty === 'Easy' ? 'text-green-400' : scenario.difficulty === 'Medium' ? 'text-yellow-400' : 'text-red-400'}>
                                    {scenario.difficulty}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Primary Objectives</h3>
                        <ul className="space-y-2">
                            {scenario.objectives.map((obj, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-white">
                                    <span className="text-indigo-500 mt-1">➤</span>
                                    {obj}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="bg-indigo-900/20 p-4 rounded border border-indigo-500/30 mb-8">
                    <p className="text-xs text-indigo-200 font-mono italic">
                        &ldquo;{scenario.voicePrompt}&rdquo;
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    {onBackToMenu && (
                        <button
                            type="button"
                            data-testid="briefing-back-to-menu"
                            onClick={() => { playUiSfx('click'); onBackToMenu(); }}
                            className={cn(buttons.ghost, 'flex-1 py-3')}
                        >
                            Back to Menu
                        </button>
                    )}
                    {onSkip && (
                        <button
                            type="button"
                            data-testid="briefing-skip"
                            onClick={() => { playUiSfx('click'); onSkip(); }}
                            className={cn(buttons.ghost, 'flex-1 py-3')}
                        >
                            Skip Briefing
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid="briefing-start"
                        onClick={() => { playUiSfx('success'); onStart(); }}
                        className={cn(buttons.primary, 'flex-[2] py-4 shadow-lg hover:shadow-indigo-500/25')}
                    >
                        Initiate Operation
                    </button>
                </div>
            </div>
        </ModalShell>
    );
};

export default ScenarioBriefingModal;